/** #@condition: vision **/
import {merge} from '#core/utils/config-utils.js';

import GenbetContent from './genbet-content.js';
import QuantumContent from './quantum-content.js';

const EventGroupsConfig = {
    defaultMatchFilters: [
        {type: 'inplay'},
        {type: 'today', selected: true},
        {type: 'custom', span: '48h'},
        {type: 'streaming'},
    ],
    matchFilters: {
        american_football: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        australian_football: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        basketball: [
            {type: 'inplay'},
            {type: 'custom', span: '2h'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'streaming'},
        ],
        boxing: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        cricket: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        cycling: [{type: 'inplay'}, {type: 'today', selected: true}, {type: 'streaming'}],
        darts: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        futsal: [
            {type: 'inplay'},
            {type: 'custom', span: '2h'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'streaming'},
        ],
        golf: [{type: 'inplay'}, {type: 'today', selected: true}, {type: 'streaming'}],
        ice_hockey: [
            {type: 'inplay'},
            {type: 'custom', span: '2h'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'streaming'},
        ],
        martial_arts: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        motorsport: [{type: 'inplay'}, {type: 'today', selected: true}, {type: 'streaming'}],
        snooker: [
            {type: 'inplay'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'custom', span: '7d'},
            {type: 'streaming'},
        ],
        soccer: [
            {type: 'inplay'},
            {type: 'custom', span: '2h'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'streaming'},
        ],
        tennis: [
            {type: 'inplay'},
            {type: 'custom', span: '2h'},
            {type: 'today', selected: true},
            {type: 'custom', span: '48h'},
            {type: 'streaming'},
        ],
        greyhound_racing: [
            {type: 'today', selected: true},
            {type: 'tomorrow'},
            {type: 'streaming', maxNumberOfEvents: 10},
        ],
        horse_racing: [{type: 'today', selected: true}, {type: 'tomorrow'}, {type: 'streaming', maxNumberOfEvents: 10}],
    },
    nextRacesFilters: {
        greyhound_racing: {
            homepage: {
                enabled: false,
            },
            highlights: {
                enabled: true,
            },
        },
        horse_racing: {
            homepage: {
                enabled: false,
            },
            highlights: {
                enabled: true,
            },
        },
    },
};

const SubscriptionDataManager = {
    items: [
        {
            subscriptionId: 'promotions_mobile',
            query: {
                collectionId: 'promotions',
                targetType: 'mobile',
            },
        },
        {
            subscriptionId: 'promotions_desktop',
            query: {
                collectionId: 'promotions',
                targetType: 'desktop',
            },
        },
        {
            subscriptionId: 'promotions_native',
            query: {
                collectionId: 'promotions',
                targetType: 'native',
            },
        },
        {
            subscriptionId: 'container_sports',
            query: {
                sportId: 'sports',
                collectionId: 'container',
                GMT: 'gmt+2',
                oddsFormat: 'decimal',
            },
            subscriptionPerfTime: 0,
        },
        ...(IS_SSBT
            ? [
                  {
                      subscriptionId: 'sportMetaInformation_sports-with-live-and-future-match-events-medium',
                      query: {
                          matchType: 'live-and-future',
                          limitFilter: 'medium',
                          collectionId: 'sportMetaInformation',
                          GMT: 'gmt+2',
                          oddsFormat: 'decimal',
                      },
                      subscriptionPerfTime: 0,
                  },
              ]
            : [
                  {
                      subscriptionId: 'sportMetaInformation_sports-with-live-match-events-small',
                      query: {
                          matchType: 'live',
                          limitFilter: 'small',
                          collectionId: 'sportMetaInformation',
                          GMT: 'gmt+2',
                          oddsFormat: 'decimal',
                      },
                      subscriptionPerfTime: 0,
                  },
                  {
                      subscriptionId: 'sportMetaInformation_sports-with-future-match-events-medium',
                      query: {
                          matchType: 'future',
                          limitFilter: 'medium',
                          collectionId: 'sportMetaInformation',
                          GMT: 'gmt+2',
                          oddsFormat: 'decimal',
                      },
                      subscriptionPerfTime: 0,
                  },
              ]),
    ],
};

const VisionConfig = {
    content: merge(GENBET_BASED ? GenbetContent : QuantumContent, {
        priceLabelDecimals: 3,
        eventGroupsConfig: EventGroupsConfig,
        search: {
            minCharsCompetitions: 3,
            minCharsEvents: 4,
            minCharsPlayers: 4,
            invalidPlayerNames: ['home', 'away', 'no goal', 'no goalscorer', 'no scorer', 'no score'],
        },
        eventNameSeparators: {
            en: {
                homeAway: 'v',
                awayHome: '@',
            },
        },
    }),
    subscriptionDataManager: SubscriptionDataManager,
};

export function buildVisionConfig(sportsContent = {}, search = {}) {
    return merge(VisionConfig, {
        content: {
            coupon: {
                config: sportsContent.couponConfig,
            },
            eventGroupsConfig: sportsContent.eventGroupsConfig,
            goal_scorer_market_type_to_mask: sportsContent.goalScorerMarketTypeToMask,
            virtualJobMap: sportsContent.virtualJobMap,
            search: search.searchOptions,
            eventNameSeparators: {
                en: sportsContent.eventNameSeparators,
            },
        },
    });
}
